import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { FileText, Check, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { QuoteWizardState } from "../DoorKnockerQuickQuote";

interface Props {
  state: QuoteWizardState;
  setState: React.Dispatch<React.SetStateAction<QuoteWizardState>>;
  onGenerate: () => void;
  isGenerating: boolean;
  progress: string;
}

export function StepTemplate({ state, setState, onGenerate, isGenerating, progress }: Props) {
  const { data: templates, isLoading } = useQuery({
    queryKey: ["proposal-templates-mobile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();
      if (!profile?.org_id) return [];
      const { data } = await supabase
        .from("proposal_templates")
        .select("id, name, description, is_default")
        .eq("org_id", profile.org_id)
        .eq("doc_type", "proposal")
        .order("is_default", { ascending: false })
        .order("name");
      return data || [];
    },
  });

  return (
    <div className="px-5 pt-6 pb-8 space-y-5">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">Step 4 / 4</div>
        <h2 className="text-2xl font-bold tracking-tight">Pick a template</h2>
        <p className="text-sm text-muted-foreground mt-1">
          We'll auto-populate the customer + measurements + line items.
        </p>
      </div>

      {isLoading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-2.5">
          {/* Default Good/Better/Best option */}
          <button
            onClick={() => setState((s) => ({ ...s, templateId: undefined }))}
            className={cn(
              "w-full flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.99]",
              !state.templateId ? "border-primary bg-primary/10" : "border-border bg-card"
            )}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">Good · Better · Best (Default)</div>
              <div className="text-[11px] text-muted-foreground">Three-tier pricing + neighborhood map</div>
            </div>
            {!state.templateId && <Check className="h-5 w-5 text-primary" />}
          </button>

          {(templates || []).map((t) => {
            const active = state.templateId === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setState((s) => ({ ...s, templateId: t.id }))}
                className={cn(
                  "w-full flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.99]",
                  active ? "border-primary bg-primary/10" : "border-border bg-card"
                )}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground">
                  <FileText className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold flex items-center gap-2">
                    {t.name}
                    {t.is_default && (
                      <span className="text-[9px] uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div className="text-[11px] text-muted-foreground truncate">{t.description}</div>
                  )}
                </div>
                {active && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}

      <Button
        onClick={onGenerate}
        disabled={isGenerating}
        className="w-full h-14 text-base font-bold gap-2 shadow-[0_8px_24px_-4px_hsl(var(--primary)/0.5)]"
        size="lg"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            {progress || "Generating proposal…"}
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            Generate Quote
          </>
        )}
      </Button>

      {isGenerating && (
        <div className="text-center text-xs text-muted-foreground space-y-1">
          <p>📡 Measuring roof from satellite…</p>
          <p>🧠 Building line items per trade…</p>
          <p>📄 Wiring up template + signature block…</p>
        </div>
      )}
    </div>
  );
}
